import { useState } from 'react';
import {
  Box, Toolbar, Button, Typography, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Snackbar, Alert,
  TableCell,
} from '@mui/material';
import { Delete, CheckCircle, Cancel, FileDownload } from '@mui/icons-material';
import { useDataProvider } from 'react-admin';

export interface BulkActionsProps {
  selectedIds: string[];
  resourceName: string;
  selectedCount: number;
  onClearSelection: () => void;
  onExportSelected?: () => void;
  onStatusChange?: () => void;
}

export const BulkActions = ({
  selectedIds,
  resourceName,
  selectedCount,
  onClearSelection,
  onExportSelected,
  onStatusChange,
}: BulkActionsProps) => {
  const dataProvider = useDataProvider();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [loading, setLoading] = useState(false);

  if (selectedCount === 0) return null;

  const handleBulkStatus = async (status: string) => {
    setLoading(true);
    try {
      await Promise.all(
        selectedIds.map(id =>
          dataProvider.update(resourceName, { id, data: { status }, previousData: {} })
        )
      );
      setSnackbar({ open: true, message: `${selectedCount} ${resourceName} updated to ${status}`, severity: 'success' });
      onClearSelection();
      onStatusChange?.();
    } catch {
      setSnackbar({ open: true, message: 'Failed to update records', severity: 'error' });
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const handleDeactivateClick = () => {
    setPendingStatus('Inactive');
    setConfirmOpen(true);
  };

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1,
          mb: 2,
          px: 2,
          minHeight: 48,
        }}
      >
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
          {selectedCount} selected
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            color="inherit"
            startIcon={<CheckCircle />}
            disabled={loading}
            onClick={() => handleBulkStatus('Active')}
          >
            Activate
          </Button>
          <Button
            size="small"
            color="inherit"
            startIcon={<Cancel />}
            disabled={loading}
            onClick={handleDeactivateClick}
          >
            Deactivate
          </Button>
          {onExportSelected && (
            <Button
              size="small"
              color="inherit"
              startIcon={<FileDownload />}
              onClick={onExportSelected}
            >
              Export Selected
            </Button>
          )}
          <Button
            size="small"
            color="inherit"
            startIcon={<Delete />}
            onClick={onClearSelection}
          >
            Clear
          </Button>
        </Box>
      </Toolbar>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivate {selectedCount} selected {resourceName}? This will set their status to Inactive.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={loading}
            onClick={() => handleBulkStatus('Inactive')}
          >
            {loading ? 'Updating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export interface SelectionCheckboxProps {
  id: string;
  selected: boolean;
  onToggle: (id: string) => void;
}

export const SelectionCheckbox = ({ id, selected, onToggle }: SelectionCheckboxProps) => (
  <TableCell padding="checkbox">
    <Checkbox
      checked={selected}
      onChange={() => onToggle(id)}
      onClick={(e) => e.stopPropagation()}
    />
  </TableCell>
);

export interface SelectAllCheckboxProps {
  items: { id: string }[];
  selectedIds: Set<string>;
  onToggleAll: (ids: string[], checked: boolean) => void;
}

export const SelectAllCheckbox = ({ items, selectedIds, onToggleAll }: SelectAllCheckboxProps) => {
  const allIds = items.map(i => i.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
  const someSelected = allIds.some(id => selectedIds.has(id));

  return (
    <TableCell padding="checkbox">
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onChange={(e) => onToggleAll(allIds, e.target.checked)}
      />
    </TableCell>
  );
};
